import React, { useState } from "react";

/**
 * ReportsSettings
 *
 * Customize or generate event reports, financial exports, and performance dashboards.
 * - Toggle different reports (show order, tabulator sheets, award printouts, etc.)
 * - Financial exports (transaction CSV, invoice PDF, etc.)
 * - Performance dashboards (enable or disable certain analytics)
 */
const ReportsSettings = () => {
  // Example toggles
  const [showOrderReport, setShowOrderReport] = useState(true);
  const [tabulatorSheets, setTabulatorSheets] = useState(true);
  const [awardPrintouts, setAwardPrintouts] = useState(false);
  const [transactionExport, setTransactionExport] = useState(true);
  const [invoiceExport, setInvoiceExport] = useState(false);
  const [enableDashboards, setEnableDashboards] = useState(true);

  // For “custom list” to detail which analytics are displayed
  const [analyticsList, setAnalyticsList] = useState([
    "Routine Counts",
    "Capacity Fill",
    "Financial Summary",
  ]);
  const [newAnalyticsItem, setNewAnalyticsItem] = useState("");

  const handleAddAnalyticsItem = () => {
    if (!newAnalyticsItem.trim()) {
      alert("Enter an analytics item name.");
      return;
    }
    setAnalyticsList([...analyticsList, newAnalyticsItem.trim()]);
    setNewAnalyticsItem("");
  };

  const handleRemoveAnalyticsItem = (idx) => {
    const updated = [...analyticsList];
    updated.splice(idx, 1);
    setAnalyticsList(updated);
  };

  const handleSave = () => {
    console.log("Reports & Analytics settings saved:", {
      showOrderReport,
      tabulatorSheets,
      awardPrintouts,
      transactionExport,
      invoiceExport,
      enableDashboards,
      analyticsList,
    });
    alert("Reports & Analytics settings saved (simulated)!");
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Page Title */}
      <h1 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
        Reporting & Analytics
      </h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
        Customize or generate event reports, financial exports, and performance dashboards.
      </p>

      {/* Available Reports */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                         rounded-lg p-6 shadow-sm space-y-4 transition-colors">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Event Reports
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Toggle which reports are available for your events.
        </p>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              id="showOrderReport"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={showOrderReport}
              onChange={(e) => setShowOrderReport(e.target.checked)}
            />
            <label htmlFor="showOrderReport" className="text-gray-700 dark:text-gray-300">
              Show Order / Run of Show
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="tabulatorSheets"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={tabulatorSheets}
              onChange={(e) => setTabulatorSheets(e.target.checked)}
            />
            <label htmlFor="tabulatorSheets" className="text-gray-700 dark:text-gray-300">
              Tabulator Sheets
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="awardPrintouts"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={awardPrintouts}
              onChange={(e) => setAwardPrintouts(e.target.checked)}
            />
            <label htmlFor="awardPrintouts" className="text-gray-700 dark:text-gray-300">
              Award Printouts
            </label>
          </div>
        </div>
      </section>

      {/* Financial Exports */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                         rounded-lg p-6 shadow-sm space-y-4 transition-colors">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Financial Exports
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Manage how transactions and invoices can be exported.
        </p>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              id="transactionExport"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={transactionExport}
              onChange={(e) => setTransactionExport(e.target.checked)}
            />
            <label htmlFor="transactionExport" className="text-gray-700 dark:text-gray-300">
              Transaction CSV Export
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="invoiceExport"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={invoiceExport}
              onChange={(e) => setInvoiceExport(e.target.checked)}
            />
            <label htmlFor="invoiceExport" className="text-gray-700 dark:text-gray-300">
              Invoice PDF Export
            </label>
          </div>
        </div>
      </section>

      {/* Performance Dashboards */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                         rounded-lg p-6 shadow-sm space-y-4 transition-colors">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Performance Dashboards
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Enable or disable real-time analytics and define which metrics are shown.
        </p>
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              id="enableDashboards"
              type="checkbox"
              className="mr-2 w-4 h-4 accent-blue-600"
              checked={enableDashboards}
              onChange={(e) => setEnableDashboards(e.target.checked)}
            />
            <label htmlFor="enableDashboards" className="text-gray-700 dark:text-gray-300">
              Enable Performance Dashboards
            </label>
          </div>

          {enableDashboards && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded space-y-3">
              <h3 className="font-medium text-gray-700 dark:text-gray-100">
                Displayed Analytics:
              </h3>
              <div className="space-y-2">
                {analyticsList.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 rounded bg-white/50 dark:bg-gray-800"
                  >
                    <span className="text-gray-700 dark:text-gray-100">{item}</span>
                    <button
                      onClick={() => handleRemoveAnalyticsItem(idx)}
                      className="text-sm text-red-500 hover:text-red-700 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-3">
                <input
                  type="text"
                  placeholder="e.g. 'City Totals'"
                  className="p-2 border border-gray-300 dark:border-gray-600 
                             rounded dark:bg-gray-700 dark:text-gray-100 
                             focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  value={newAnalyticsItem}
                  onChange={(e) => setNewAnalyticsItem(e.target.value)}
                />
                <button
                  onClick={handleAddAnalyticsItem}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                             rounded transition-colors font-medium"
                >
                  Add Metric
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                     font-semibold rounded shadow transition-colors"
        >
          Save Reports Settings
        </button>
      </div>
    </div>
  );
};

export default ReportsSettings;
